import '../styles/panda.css';
import '@zillow/constellation-fonts/zillow-fonts.css';

import type { AppProps } from 'next/app';
import type { PrefetchQueriesProps } from '@zg-rentals/next-react-query5';
import { useRouter } from 'next/router';
import { CookiesProvider } from 'react-cookie';
import { Head } from '@zg-rentals/next/pages';
import { Auth, useAuth } from '../AuthProvider';
import { useZrmPageViewEvents } from '@zg-rentals/clickstream';
import { useComscorePageTracking } from '@zg-rentals/comscore';
import { ToastProvider } from '@zillow/constellation-10';
import { DefaultQueryProvider } from '@zg-rentals/react-query5/client';
import { HydrationBoundary } from '@zg-rentals/react-query5';
import { useRegisterNotification } from '@zg-rentals/register-notification';
import { RpErrorBoundary, useBootstrap } from '@zg-rentals/rp-bootstrap-browser';
import { useSiftScience } from '@zg-rentals/sift-science';
import { trackComscoreEvent, trackUserEvent } from '../analytics';
import { EsusuProvider } from '../contexts/EsusuContext';
import EsusuLayout from '../components/EsusuLayout';

const UserAnalytics = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAuth();
  const router = useRouter();
  const { email, impersonatorUserToken } = user ?? {};

  useSiftScience({ email, isImpersonator: Boolean(impersonatorUserToken) });
  useZrmPageViewEvents(trackUserEvent, router.pathname, user);
  useComscorePageTracking(() => trackComscoreEvent({}), router.pathname);
  useRegisterNotification({ ref: 'YOUR_RENTAL_WEB' });

  return children;
};

const MyApp = ({ Component, pageProps: { state, ...pageProps } }: AppProps<PrefetchQueriesProps>) => {
  useBootstrap(() => import('../bootstrap/browser'));

  return (
    <>
      <Head applicationName="your-rental-web" buildRef={process.env.NEXT_PUBLIC_BUILD_REF}>
        <link rel="preconnect" href="https://www.zillowstatic.com/" crossOrigin="anonymous" />
        <link
          rel="preload"
          href="https://www.zillowstatic.com/s3/constellation-website/public/shared/fonts/inter/latest/inter-variable.woff2"
          as="font"
          type="font/woff2"
          crossOrigin="anonymous"
        />
        <link
          rel="preload"
          href="https://www.zillowstatic.com/s3/constellation-website/public/shared/fonts/object-sans/latest/object-sans-heavy.woff2"
          as="font"
          type="font/woff2"
          crossOrigin="anonymous"
        />
      </Head>
      <RpErrorBoundary>
        <DefaultQueryProvider>
          <HydrationBoundary state={state}>
            <Auth.Provider>
              <EsusuProvider>
                <EsusuLayout>
                  <UserAnalytics>
                    <ToastProvider placement="bottom-end">
                      <CookiesProvider>
                        <Component {...pageProps} />
                      </CookiesProvider>
                    </ToastProvider>
                  </UserAnalytics>
                </EsusuLayout>
              </EsusuProvider>
            </Auth.Provider>
          </HydrationBoundary>
        </DefaultQueryProvider>
      </RpErrorBoundary>
    </>
  );
};

export default MyApp;
